import {
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  Type,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { IWizardStepDefinition, IWizardStepState, WizardStepComponent } from './interfaces/IWizard';
import { WizardContext } from './services/wizard-context';
import { IStepItem } from '../zn-stepper/interfaces/IStepItem';
import { ZnStepper } from '../zn-stepper/zn-stepper';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { ZnButton } from '../zn-button/zn-button';
import { LocalizationPipe } from '@abp/ng.core';
import { firstValueFrom, isObservable } from 'rxjs';

@Component({
  selector: 'zn-stepper-wizard',
  standalone: true,
  imports: [ZnStepper, ZnButton, LocalizationPipe],
  providers: [WizardContext],
  templateUrl: './zn-stepper-wizard.html',
  styleUrl: './zn-stepper-wizard.scss',
})
export class ZnStepperWizard implements OnInit {
  readonly stepDefinitions = input.required<IWizardStepDefinition[]>();
  readonly storageKey = input<string | null>();
  readonly finalActionLabelKey = input<string>(
    `${ZendraLocalizationServices.LanguageService}::Confirm`,
  );
  readonly outlet = viewChild.required('stepHost', { read: ViewContainerRef });
  
  readonly submitWizardData = output<Record<number, unknown>>();
  readonly cancelWizard = output();

  readonly wizardService = inject(WizardContext);
  readonly navigationInProgress = signal(false);
  readonly persistenceReady = signal(false);

  readonly visibleSteps = computed(() => {
    return this.stepDefinitions().filter(step => !this.isStepHidden(step));
  });
  readonly visibleStepItems = computed<IStepItem[]>(() => {
    return this.visibleSteps().map(step => this.buildStepItem(step));
  });
  readonly activeVisibleIndex = computed(() => {
    const activeId = this.wizardService.activeStepId();
    const activeIndex = this.visibleSteps().findIndex(step => step.id === activeId);
    return Math.max(activeIndex, 0);
  });
  readonly isFirstStep = computed(() => this.activeVisibleIndex() === 0);
  readonly isLastStep = computed(() =>
    this.activeVisibleIndex() === this.visibleSteps().length - 1
  );

  localizationResource = ZendraLocalizationServices.LanguageService;

  private activeStepReference: ComponentRef<WizardStepComponent>;
  private readonly persistWizardState = effect(() => {
    if (!this.persistenceReady()) {
      return;
    }

    const storageKey = this.storageKey();
    if (!storageKey) {
      return;
    }

    this.writePersistedState(storageKey);
  });

  ngOnInit(): void {
    const visibleSteps = this.visibleSteps();
    const firstStep = visibleSteps[0];
    if (!firstStep) return;

    const restoredStepId = this.restorePersistedState();
    const initialStep = visibleSteps.find(step => step.id === restoredStepId) ?? firstStep;

    this.wizardService.goTo(initialStep.id);
    this.loadStep(initialStep);
    this.persistenceReady.set(true);
  }

  async navigateNext() {
    const currentStep = this.activeVisibleIndex();
    const currentStepDefinition = this.visibleSteps()[currentStep];

    if (this.navigationInProgress()) return;

    this.navigationInProgress.set(true);

    try {
      if (!(await this.checkCanLeave())) return;

      this.collectAndSaveCurrentStep(currentStepDefinition);
      this.deactivateCurrentStep();

      if (this.isLastStep()) {
        this.submitWizard();
        return;
      }

      const nextStep = this.visibleSteps()[currentStep + 1];
      this.wizardService.goTo(nextStep.id);
      this.loadStep(nextStep);
    } finally {
      this.navigationInProgress.set(false);
    }
  }

  navigatePrevious() {
    const currentStep = this.activeVisibleIndex();
    const currentStepDefinition = this.visibleSteps()[currentStep];

    if (this.isFirstStep()) return;

    this.collectAndSaveCurrentStep(currentStepDefinition);
    this.deactivateCurrentStep();

    const prevStep = this.visibleSteps()[this.activeVisibleIndex() - 1];

    this.wizardService.goTo(prevStep.id);
    this.loadStep(prevStep);
  }

  cancel() {
    this.cancelWizard.emit();
  }

  hasPendingChanges(): boolean {
    const activeInstance = this.activeStepReference?.instance;
    if (activeInstance?.hasMeaningfulData?.(this.wizardService)) {
      return true;
    }

    return this.stepDefinitions().some(step => {
      const state = this.wizardService.getStepState(step.id);
      return this.hasMeaningfulStepState(state);
    });
  }

  clearPersistedState(): void {
    const storageKey = this.storageKey();
    if (!storageKey || !this.canUseSessionStorage()) {
      return;
    }

    window.sessionStorage.removeItem(storageKey);
  }

  discardState(): void {
    // Prevent the reset below from being persisted while the host redirects away.
    this.persistenceReady.set(false);
    this.wizardService.reset();
    this.clearPersistedState();
  }

  async onStepperClick(visibleIndex: number): Promise<void> {
    const targetStep = this.visibleSteps()[visibleIndex];
    if (!targetStep) return;
    if (visibleIndex === this.activeVisibleIndex()) return;
    if (this.navigationInProgress()) return;

    this.navigationInProgress.set(true);

    try {
      if (!(await this.checkCanLeave())) return;

      const currentStepDefinition = this.visibleSteps()[this.activeVisibleIndex()];
      if (currentStepDefinition) {
        this.collectAndSaveCurrentStep(currentStepDefinition);
      }

      this.deactivateCurrentStep();

      this.wizardService.goTo(targetStep.id);
      this.loadStep(targetStep);
    } finally {
      this.navigationInProgress.set(false);
    }
  }

  private loadStep(step: IWizardStepDefinition): void {
    this.outlet().clear();

    const StepReference = this.outlet().createComponent(
      step.component as Type<WizardStepComponent>
    );

    this.applyStepInputs(StepReference, step);
    this.activeStepReference = StepReference;

    StepReference.instance.onActivate?.(this.wizardService);
  }

  private applyStepInputs(
    reference: ComponentRef<WizardStepComponent>,
    step: IWizardStepDefinition
  ): void {
    if (!step.inputs) return;

    const inputs = typeof step.inputs === 'function'
      ? step.inputs(this.wizardService)
      : step.inputs;

    for (const [key, value] of Object.entries(inputs as object)) {
      reference.setInput(key, value);
    }
  }

  async checkCanLeave(): Promise<boolean> {
    const instance = this.activeStepReference?.instance;
    if (!instance?.canLeave) return true;

    try {
      return await this.resolveCanLeaveResult(instance.canLeave(this.wizardService));
    } catch {
      return false;
    }
  }

  checkCanLeaveSync(): boolean {
    const instance = this.activeStepReference?.instance;
    if (!instance) return true;

    if (instance.canLeaveSync) {
      return instance.canLeaveSync(this.wizardService);
    }

    if (!instance.canLeave) {
      return true;
    }

    const result = instance.canLeave(this.wizardService);
    return typeof result === 'boolean' ? result : true;
  }

  isNextDisabled(): boolean {
    return this.navigationInProgress() || !this.checkCanLeaveSync();
  }

  private collectAndSaveCurrentStep(step: IWizardStepDefinition) {
    const instance = this.activeStepReference?.instance;
    if (!instance?.collect) return;

    const data    = instance.collect(this.wizardService);
    const summary = instance.buildSummary?.(data, this.wizardService) ?? [];

    this.wizardService.patchStepState(step.id, {
      data,
      summary,
      visited:   true,
      completed: true,
    });
  }

  private deactivateCurrentStep() {
    this.activeStepReference?.instance.onDeactivate?.(this.wizardService);
  }

  private async resolveCanLeaveResult(result: ReturnType<NonNullable<WizardStepComponent['canLeave']>>): Promise<boolean> {
    if (isObservable(result)) {
      return firstValueFrom(result);
    }

    return await result;
  }

  private submitWizard(): void {
    this.submitWizardData.emit(this.wizardService.allData());
  }

  private buildStepItem(step: IWizardStepDefinition): IStepItem {
    const state = this.wizardService.getStepState(step.id);

    return {
      id: step.id,
      title: step.title,
      pills: state?.summary ?? [],
      optional: step.optional,
    };
  }

  private isStepHidden(step: IWizardStepDefinition): boolean {
    if (typeof step.hidden === 'function') {
      return step.hidden(this.wizardService);
    }
    return step.hidden ?? false;
  }

  private hasMeaningfulStepState(state?: {
    dirty: boolean;
    completed: boolean;
    data?: unknown;
    summary?: unknown[];
  }): boolean {
    if (!state) {
      return false;
    }

    const hasSummary = (state.summary?.length ?? 0) > 0;
    const hasData = this.hasMeaningfulValue(state.data);

    return state.completed || (state.dirty && (hasData || hasSummary));
  }

  private hasMeaningfulValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return true;
  }

  private restorePersistedState(): number | null {
    const storageKey = this.storageKey();
    if (!storageKey || !this.canUseSessionStorage()) {
      return null;
    }

    const serializedState = window.sessionStorage.getItem(storageKey);
    if (!serializedState) {
      return null;
    }

    try {
      const parsedState = JSON.parse(serializedState) as {
        activeStepId?: number;
        stepStates?: Record<number, unknown>;
      };

      if (
        typeof parsedState.activeStepId !== 'number' ||
        typeof parsedState.stepStates !== 'object' ||
        parsedState.stepStates === null
      ) {
        this.clearPersistedState();
        return null;
      }

      this.wizardService.restore({
        activeStepId: parsedState.activeStepId,
        stepStates: parsedState.stepStates as Record<number, IWizardStepState>,
      });

      return parsedState.activeStepId;
    } catch {
      this.clearPersistedState();
      return null;
    }
  }

  private writePersistedState(storageKey: string): void {
    if (!this.canUseSessionStorage()) {
      return;
    }

    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify(this.wizardService.snapshot()),
    );
  }

  private canUseSessionStorage(): boolean {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  }
}
